/**
 * @fileoverview Atlas Content Admin
 * @module AtlasContentAdmin
 *
 * Admin interface for managing Atlas educational content.
 * Allows super-admins to view, edit, and manage:
 * - Products (name, description, capabilities, evidence, caveats)
 * - Capabilities
 * - Frameworks
 * - Personas
 * - Glossary terms
 *
 * Uses the Atlas API for CRUD operations when enabled,
 * falls back to read-only view of bundled JSON otherwise.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Package,
  Zap,
  FileText,
  Users,
  BookOpen,
  Edit3,
  Eye,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  X,
} from 'lucide-react';
import Fuse from 'fuse.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import * as contentLoader from '../Atlas/lib/contentLoader';
import { checkApiHealth, getApiEnabled, setApiEnabled } from '../Atlas/lib/atlasApi';
import './AtlasContentAdmin.css';

/**
 * Tab configuration
 */
const TABS = [
  { id: 'products', label: 'Products', icon: Package },
  { id: 'capabilities', label: 'Capabilities', icon: Zap },
  { id: 'frameworks', label: 'Frameworks', icon: FileText },
  { id: 'personas', label: 'Personas', icon: Users },
  { id: 'glossary', label: 'Glossary', icon: BookOpen },
];

/**
 * Pillar color mapping
 */
const PILLAR_COLORS = {
  network: '#06b6d4',
  cloud: '#10b981',
  'security-ops': '#3b82f6',
  identity: '#9333ea',
  'ai-security': '#f59e0b',
  'data-protection': '#ec4899',
  'incident-response': '#ef4444',
};

/**
 * Product Edit Dialog
 */
function ProductEditDialog({ product, open, onClose, onSave, isSaving }) {
  const [formData, setFormData] = useState(product || {});

  React.useEffect(() => {
    if (product) setFormData(product);
  }, [product]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value) => {
    const arr = value.split(',').map(s => s.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, [field]: arr }));
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Edit3 size={20} />
        Edit Product: {product.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
          />
          <TextField
            label="Pillar"
            value={formData.pillar || ''}
            onChange={(e) => handleChange('pillar', e.target.value)}
            fullWidth
            size="small"
            helperText="network, cloud, identity, security-ops, ai-security, data-protection, incident-response"
          />
          <TextField
            label="Maturity"
            value={formData.maturity || ''}
            onChange={(e) => handleChange('maturity', e.target.value)}
            fullWidth
            size="small"
            helperText="mature, ga, emerging, roadmap"
          />
          <TextField
            label="Confidence (0-1)"
            type="number"
            value={formData.confidence || ''}
            onChange={(e) => handleChange('confidence', parseFloat(e.target.value))}
            fullWidth
            size="small"
            inputProps={{ min: 0, max: 1, step: 0.1 }}
          />
          <TextField
            label="Evidence (comma-separated)"
            value={(formData.evidence || []).join(', ')}
            onChange={(e) => handleArrayChange('evidence', e.target.value)}
            fullWidth
            size="small"
            helperText="e.g., Gartner MQ 2025, Forrester Wave Q1 2025"
          />
          <TextField
            label="Caveats (comma-separated)"
            value={(formData.caveats || []).join(', ')}
            onChange={(e) => handleArrayChange('caveats', e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            helperText="Important: Always include relevant caveats"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={() => onSave(formData)}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <CheckCircle size={16} />}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Product View Dialog
 */
function ProductViewDialog({ product, open, onClose }) {
  if (!product) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Eye size={20} />
        {product.name}
        <Chip
          label={product.pillar}
          size="small"
          sx={{ ml: 1, bgcolor: PILLAR_COLORS[product.pillar] || '#6b7280', color: 'white' }}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
            <Typography variant="body2">{product.description || 'No description'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Maturity</Typography>
              <Chip label={product.maturity || 'N/A'} size="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Confidence</Typography>
              <Chip
                label={`${((product.confidence || 0) * 100).toFixed(0)}%`}
                size="small"
                color={product.confidence >= 0.8 ? 'success' : product.confidence >= 0.5 ? 'warning' : 'error'}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Capabilities</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {(product.capabilities || []).map(cap => (
                <Chip key={cap} label={cap} size="small" variant="outlined" />
              ))}
              {(!product.capabilities || product.capabilities.length === 0) && (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Evidence</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {(product.evidence || []).map((ev, i) => (
                <Chip key={i} label={ev} size="small" color="info" variant="outlined" />
              ))}
              {(!product.evidence || product.evidence.length === 0) && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No direct evaluation found.
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AlertTriangle size={14} /> Caveats
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {(product.caveats || []).map((caveat, i) => (
                <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  • {caveat}
                </Typography>
              ))}
              {(!product.caveats || product.caveats.length === 0) && (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Main AtlasContentAdmin component
 */
function AtlasContentAdmin() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogMode, setDialogMode] = useState(null); // 'view' | 'edit'
  const [apiEnabled, setApiEnabledState] = useState(getApiEnabled());

  // Check API health
  const { data: apiHealth, refetch: refetchHealth } = useQuery({
    queryKey: ['atlas', 'health'],
    queryFn: checkApiHealth,
    staleTime: 60000,
  });

  // Fetch products (from API or JSON)
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['atlas', 'products'],
    queryFn: async () => {
      if (apiEnabled && apiHealth?.status === 'healthy') {
        const response = await apiClient.get('/api/atlas/products');
        return response.products || [];
      }
      return contentLoader.getProducts();
    },
    enabled: activeTab === 'products',
  });

  // Fetch capabilities
  const { data: capabilities = [], isLoading: capabilitiesLoading } = useQuery({
    queryKey: ['atlas', 'capabilities'],
    queryFn: async () => {
      if (apiEnabled && apiHealth?.status === 'healthy') {
        const response = await apiClient.get('/api/atlas/capabilities');
        return response.capabilities || [];
      }
      return contentLoader.getCapabilities();
    },
    enabled: activeTab === 'capabilities',
  });

  // Fetch frameworks
  const { data: frameworks = [], isLoading: frameworksLoading } = useQuery({
    queryKey: ['atlas', 'frameworks'],
    queryFn: async () => {
      if (apiEnabled && apiHealth?.status === 'healthy') {
        const response = await apiClient.get('/api/atlas/frameworks');
        return response.frameworks || [];
      }
      return contentLoader.getFrameworks();
    },
    enabled: activeTab === 'frameworks',
  });

  // Fetch personas
  const { data: personas = [], isLoading: personasLoading } = useQuery({
    queryKey: ['atlas', 'personas'],
    queryFn: async () => {
      if (apiEnabled && apiHealth?.status === 'healthy') {
        const response = await apiClient.get('/api/atlas/personas');
        return response.personas || [];
      }
      return contentLoader.getPersonas();
    },
    enabled: activeTab === 'personas',
  });

  // Fetch glossary
  const { data: glossary = [], isLoading: glossaryLoading } = useQuery({
    queryKey: ['atlas', 'glossary'],
    queryFn: async () => {
      if (apiEnabled && apiHealth?.status === 'healthy') {
        const response = await apiClient.get('/api/atlas/glossary');
        return response.terms || [];
      }
      return contentLoader.getGlossaryTerms();
    },
    enabled: activeTab === 'glossary',
  });

  // Get current data based on active tab
  const currentData = useMemo(() => {
    switch (activeTab) {
      case 'products': return products;
      case 'capabilities': return capabilities;
      case 'frameworks': return frameworks;
      case 'personas': return personas;
      case 'glossary': return glossary;
      default: return [];
    }
  }, [activeTab, products, capabilities, frameworks, personas, glossary]);

  // Setup Fuse search
  const fuse = useMemo(() => {
    const keys = activeTab === 'products'
      ? ['name', 'description', 'pillar']
      : activeTab === 'glossary'
      ? ['term', 'definition']
      : ['name', 'description'];
    return new Fuse(currentData, { keys, threshold: 0.4 });
  }, [currentData, activeTab]);

  // Filtered data
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return currentData;
    return fuse.search(searchQuery).map(r => r.item);
  }, [currentData, searchQuery, fuse]);

  const isLoading = activeTab === 'products' ? productsLoading
    : activeTab === 'capabilities' ? capabilitiesLoading
    : activeTab === 'frameworks' ? frameworksLoading
    : activeTab === 'personas' ? personasLoading
    : glossaryLoading;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery('');
    setSelectedItem(null);
  };

  const handleView = useCallback((item) => {
    setSelectedItem(item);
    setDialogMode('view');
  }, []);

  const handleEdit = useCallback((item) => {
    setSelectedItem(item);
    setDialogMode('edit');
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedItem(null);
    setDialogMode(null);
  }, []);

  const handleToggleApi = () => {
    const newValue = !apiEnabled;
    setApiEnabled(newValue);
    setApiEnabledState(newValue);
    queryClient.invalidateQueries({ queryKey: ['atlas'] });
  };

  // Save mutation (placeholder - requires backend write endpoints in Step 3.2)
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      // TODO: Implement write endpoint when backend supports it
      console.log('Would save:', data);
      throw new Error('Write operations not yet implemented');
    },
    onMutate: (data) => {
      // Optimistic — patch the cached product and close the dialog immediately;
      // roll back (and surface the error) if the write fails.
      const previous = queryClient.getQueryData(['atlas', 'products']);
      if (Array.isArray(previous)) {
        queryClient.setQueryData(
          ['atlas', 'products'],
          previous.map((p) => (p.id === data.id ? { ...p, ...data } : p))
        );
      }
      handleCloseDialog();
      return { previous };
    },
    onError: (_err, _data, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['atlas', 'products'], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['atlas', 'products'] });
    },
  });

  const handleSave = (data) => {
    saveMutation.mutate(data);
  };

  return (
    <Box className="atlas-content-admin">
      {/* Header */}
      <Box className="atlas-content-admin__header">
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Atlas Content Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage educational content for the Cybersecurity Atlas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* API Status */}
          <Chip
            icon={apiHealth?.status === 'healthy' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            label={apiHealth?.status === 'healthy' ? 'API Connected' : 'Using JSON'}
            color={apiHealth?.status === 'healthy' ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
          <Tooltip title={apiEnabled ? 'Disable API mode' : 'Enable API mode'}>
            <IconButton size="small" onClick={handleToggleApi}>
              <RefreshCw size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* API Mode Alert */}
      {apiHealth?.status !== 'healthy' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Atlas API not available. Viewing bundled JSON data (read-only). Run the migration and seed script to enable database mode.
        </Alert>
      )}

      {/* Save failure — the optimistic edit has been rolled back */}
      {saveMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => saveMutation.reset()}>
          Save failed: {saveMutation.error?.message || 'unknown error'}. Your change was not applied.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                icon={<Icon size={18} />}
                label={tab.label}
                iconPosition="start"
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* Search */}
      <TextField
        placeholder={`Search ${activeTab}...`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={18} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchQuery('')}>
                <X size={16} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Content Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {activeTab === 'products' && (
                <>
                  <TableCell>Name</TableCell>
                  <TableCell>Pillar</TableCell>
                  <TableCell>Maturity</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Evidence</TableCell>
                  <TableCell>Caveats</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </>
              )}
              {activeTab === 'capabilities' && (
                <>
                  <TableCell>Name</TableCell>
                  <TableCell>Pillar</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </>
              )}
              {activeTab === 'frameworks' && (
                <>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </>
              )}
              {activeTab === 'personas' && (
                <>
                  <TableCell>Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Priorities</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </>
              )}
              {activeTab === 'glossary' && (
                <>
                  <TableCell>Term</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Definition</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? `No ${activeTab} match "${searchQuery}"` : `No ${activeTab} found`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id} hover>
                  {activeTab === 'products' && (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.pillar}
                          size="small"
                          sx={{ bgcolor: PILLAR_COLORS[item.pillar] || '#6b7280', color: 'white', fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={item.maturity || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${((item.confidence || 0) * 100).toFixed(0)}%`}
                          size="small"
                          color={item.confidence >= 0.8 ? 'success' : item.confidence >= 0.5 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {item.evidence?.length || 0} items
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {item.caveats?.length || 0} items
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(item)}>
                            <Edit3 size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                  {activeTab === 'capabilities' && (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.pillar}
                          size="small"
                          sx={{ bgcolor: PILLAR_COLORS[item.pillar] || '#6b7280', color: 'white', fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {item.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                  {activeTab === 'frameworks' && (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.version || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                          {item.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                  {activeTab === 'personas' && (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{item.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {item.priorities?.length || 0} priorities
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                  {activeTab === 'glossary' && (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.term}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.category || 'General'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                          {item.definition}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Record count */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {filteredData.length} of {currentData.length} {activeTab}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Mode: {apiHealth?.status === 'healthy' ? 'Database' : 'Bundled JSON'}
        </Typography>
      </Box>

      {/* Dialogs */}
      {activeTab === 'products' && (
        <>
          <ProductViewDialog
            product={selectedItem}
            open={dialogMode === 'view'}
            onClose={handleCloseDialog}
          />
          <ProductEditDialog
            product={selectedItem}
            open={dialogMode === 'edit'}
            onClose={handleCloseDialog}
            onSave={handleSave}
            isSaving={saveMutation.isPending}
          />
        </>
      )}
    </Box>
  );
}

export default AtlasContentAdmin;
